import { FC } from 'react'
import {
  Button, Datagrid, DateField, FunctionField, NumberField, TextField,
  useGetList, useList, ListContextProvider, Pagination, useTranslate,
  useRefresh, useNotify, useDelete, useRecordContext,
} from 'react-admin'
import { useNavigate } from 'react-router-dom'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import { IconButton, Tooltip } from '@mui/material'
import { EnabledChip } from './Chips'
import { OrganizationMcpLinkOut } from '../types/apiRequests'

interface McpLinksDatagridProps {
  filter: Record<string, unknown>
  createState: Record<string, unknown>
  showOrganization?: boolean
  showServer?: boolean
}

const DeleteRowButton: FC = () => {
  const record = useRecordContext<{ id: string }>()
  const refresh = useRefresh()
  const notify = useNotify()
  const translate = useTranslate()
  const [deleteOne] = useDelete()

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (record?.id && window.confirm(translate('delete.confirmTitle'))) {
      void deleteOne('organization_mcp_link', { id: record.id }, {
        mutationMode: 'pessimistic',
        // eslint-disable-next-line camelcase
        onSuccess: () => { notify('ra.notification.deleted', { type: 'info', messageArgs: { smart_count: 1 } }); refresh() },
      })
    }
  }

  return (
    <Tooltip title={translate('ra.action.delete')}>
      <IconButton size="small" onClick={handleDelete} color="error">
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  )
}

const EditRowButton: FC = () => {
  const record = useRecordContext<{ id: string }>()
  const navigate = useNavigate()
  const translate = useTranslate()

  if (!record?.id) return null

  return (
    <Tooltip title={translate('ra.action.edit')}>
      <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/organization_mcp_link/${String(record.id)}`) }}>
        <EditIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  )
}

export const McpLinksDatagrid: FC<McpLinksDatagridProps> = ({ filter, createState, showOrganization = false, showServer = false }) => {
  const navigate = useNavigate()
  const translate = useTranslate()

  const { data, isLoading } = useGetList<OrganizationMcpLinkOut>(
    'organization_mcp_link',
    { pagination: { page: 1, perPage: 100 }, sort: { field: 'created', order: 'DESC' }, filter },
    { enabled: Object.values(filter).every(Boolean) }
  )

  const listContext = useList({ data: data ?? [], isLoading, perPage: 25 })

  return (
    <>
      <Button
        label="resources.organization_mcp_link.create"
        onClick={() => navigate('/organization_mcp_link/create', { state: { record: createState } })}
        sx={{ mb: 2 }}
      >
        <AddIcon />
      </Button>
      <ListContextProvider value={listContext}>
        <Datagrid
          bulkActionButtons={false}
          rowClick={(id) => { navigate(`/organization_mcp_link/${String(id)}`); return false }}
        >
          {showOrganization && <TextField source="organization_display_name" label="data.organization" />}
          {showServer && <TextField source="server_display_name" label="data.mcpServer" />}
          <TextField source="base_url" />
          <NumberField source="user_count" label="data.users" />
          <FunctionField label="data.enabled" render={() => <EnabledChip />} />
          <DateField source="created" showTime label="data.createdAt" />
          <EditRowButton />
          <DeleteRowButton />
        </Datagrid>
        <Pagination />
      </ListContextProvider>
    </>
  )
}
